import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchSharedCart, scheduleDeferredTask } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    super.disconnectedCallback();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #scheduleDrawerSideEffects() {
    // Keep drawer open paint free of Monetate / Rokt / extra /cart.js work.
    scheduleDeferredTask(
      () => {
        const { dialog } = this.refs;
        if (!dialog?.open) return;
        this.#trackCartDrawerOpen();
        // Rokt scope correction (FMN): the cart drawer is not one of Rokt's agreed contexts
        // (Checkout, Thank You, Order Status, Orders, Profile). To restore, uncomment.
        // this.#requestRoktAdZone();
      },
      { delay: 350, timeout: 2000 }
    );
  }

  #requestRoktAdZone() {
    const roktPlaceholder = this.querySelector('#rokt-placeholder');
    const iFrameLoaded = this.querySelector('#rokt-placeholder iframe');
    if (roktPlaceholder && !iFrameLoaded && typeof dataLayer !== 'undefined') {
      window.dataLayer.push({ event: 'roktAdZone' });
    }
  }

  showDialog() {
    super.showDialog();
    this.#scheduleDrawerSideEffects();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    // Ensure backdrop click closes the drawer immediately
    const { dialog } = this.refs;
    if (dialog) {
      // Handle backdrop clicks - when clicking the backdrop, event.target is the dialog element
      const handleBackdropClick = (event) => {
        if (event.target === dialog) {
          this.closeDialog();
        }
      };
      // Add immediately without setTimeout to ensure it works right away
      dialog.addEventListener('click', handleBackdropClick);
    }
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #trackCartDrawerOpen = async () => {
    if (!window.monetateQ) return;
    try {
      const cart = await fetchSharedCart();
      const isEmpty = !cart || cart.item_count === 0;
      window.monetateQ.push(['setPageType', isEmpty ? 'EMPTYCART' : 'CART']);
      if (window.__monetateBaseVars) {
        window.monetateQ.push(['setCustomVariables', window.__monetateBaseVars]);
      }
      const items = Array.isArray(cart.items) ? cart.items : [];
      // resolveMonetateProductId (from monetate.liquid) checks the SSR map first, then falls back to a
      // live lookup for items added after the page/drawer last rendered - the static map alone can't
      // know about those, which is exactly the case when the drawer opens right after an add.
      const resolveId =
        typeof window.resolveMonetateProductId === 'function'
          ? window.resolveMonetateProductId
          : async (productId) => (window.__monetateCartIdMap || {})[productId] ?? null;
      const resolvedIds = await Promise.all(items.map((item) => resolveId(item.product_id)));
      const cartRows = items
        .map((item, index) => ({ item, monetateId: resolvedIds[index] }))
        .filter(({ monetateId }) => monetateId != null)
        .map(({ item, monetateId }) => ({
          productId: monetateId,
          quantity: item.quantity,
          unitPrice: item.final_price / 100,
          currency: cart.currency || cart.currency_code || '',
        }));
      if (cartRows.length) {
        window.monetateQ.push(['addCartRows', cartRows]);
      }
      window.monetateQ.push(['trackData']);
    } catch (error) {
      // Silent fail; avoid breaking cart drawer behavior.
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
